"use client"

import { ReactNode, useEffect, useId, useRef } from "react"
import { Platform, Switch as S, SwitchProps } from "react-native"
import { styled } from "nativewind"

import { colors } from "@bullseye/tailwind-config"

import { Box } from "../Layout"
import { P } from "../Typography"

// extends SwitchProps with properties available using rnw
// see: https://necolas.github.io/react-native-web/docs/switch/
type WebSwitch = SwitchProps & {
  activeThumbColor?: string
  activeTrackColor?: string
}

const Switch = styled(S as React.ComponentType<WebSwitch>)

type Props = {
  value: boolean
  onChange: (value: boolean) => void
  "aria-labelledby"?: string
  "aria-label"?: string
}

type Switch = SwitchProps & {
  activeThumbColor?: string
}

export const Toggle = ({
  value,
  onChange,
  "aria-labelledby": ariaLabelledBy,
  "aria-label": ariaLabel,
}: Props) => {
  const ref = useRef<any>(null)

  useEffect(() => {
    if (Platform.OS !== "web" || !ref.current) return
    const input = (ref.current as HTMLElement).querySelector?.("input")
    if (!input) return
    if (ariaLabel) {
      input.setAttribute("aria-label", ariaLabel)
    } else {
      input.removeAttribute("aria-label")
    }
    if (ariaLabelledBy) {
      input.setAttribute("aria-labelledby", ariaLabelledBy)
    } else {
      input.removeAttribute("aria-labelledby")
    }
  }, [ariaLabel, ariaLabelledBy])

  return (
    <Box ref={ref}>
      <Switch
        trackColor={{ false: colors.gray[50], true: colors.primary[700] }}
        thumbColor={colors.white}
        ios_backgroundColor={colors.gray[50]}
        activeThumbColor={colors.white}
        onValueChange={onChange}
        value={value}
      />
    </Box>
  )
}

type ToggleWithLabelProps = Props & {
  label: ReactNode
}

export const ToggleWithLabel = ({ label, ...rest }: ToggleWithLabelProps) => {
  const labelId = useId()
  return (
    <Box className="min-h-14 h-14 flex-row items-center justify-between rounded-lg border border-solid border-gray-100 px-2 g-4">
      <P nativeID={labelId}>{label}</P>
      <Toggle {...rest} aria-labelledby={labelId} />
    </Box>
  )
}
